const TURKISH_MOBILE_DIGITS_LENGTH = 10;
const TURKISH_MOBILE_PREFIX = '5';
const TURKISH_MOBILE_E164_PREFIX = '+90';

export function validateTurkishMobilePhone(value: string): { valid: boolean; message?: string } {
  if (!value || !value.trim()) {
    return { valid: true };
  }
  const trimmed = value.trim();
  let digits = trimmed.replace(/\D/g, '');
  if (digits.length === 0 || /[a-zA-Z]/.test(trimmed)) {
    return { valid: false, message: 'Telefon numarası sadece rakam içermelidir.' };
  }
  if (digits.startsWith('90') && digits.length > TURKISH_MOBILE_DIGITS_LENGTH) {
    digits = digits.slice(2);
  }
  if (digits.startsWith('0') && digits.length > TURKISH_MOBILE_DIGITS_LENGTH) {
    digits = digits.slice(1);
  }
  if (digits.length !== TURKISH_MOBILE_DIGITS_LENGTH) {
    return {
      valid: false,
      message: `Türkiye mobil numarası ${TURKISH_MOBILE_DIGITS_LENGTH} haneli olmalıdır (örn: 5XX XXX XX XX).`,
    };
  }
  if (!digits.startsWith(TURKISH_MOBILE_PREFIX)) {
    return {
      valid: false,
      message: 'Türkiye mobil numarası 5 ile başlamalıdır (örn: 555 123 45 67).',
    };
  }
  const secondDigit = digits[1];
  if (!['0', '2', '3', '4', '5'].includes(secondDigit)) {
    return {
      valid: false,
      message: 'Geçerli bir mobil operatör kodu giriniz (5XX).',
    };
  }
  return { valid: true };
}

export function normalizePhoneInput(value: string): string {
  const hasPlus = value.trimStart().startsWith('+');
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length > 10) {
    digits = digits.slice(2);
  }
  if (digits.startsWith('0') && digits.length > 10) {
    digits = digits.slice(1);
  }
  if (digits.length > TURKISH_MOBILE_DIGITS_LENGTH) {
    digits = digits.slice(0, TURKISH_MOBILE_DIGITS_LENGTH);
  }
  if (!digits) return hasPlus ? '+' : '';
  return hasPlus ? `${TURKISH_MOBILE_E164_PREFIX}${digits}` : digits;
}

const TURKISH_PLATE_REGEX = /^(0[1-9]|[1-7][0-9]|8[01])[A-Za-z]{1,3}\d{2,4}$/;
const TURKISH_PLATE_MIN_LENGTH = 5;
const TURKISH_PLATE_MAX_LENGTH = 9;
const TURKISH_PLATE_INPUT_MAX_LENGTH = 11;

export function validateTurkishPlate(value: string): { valid: boolean; message?: string } {
  if (!value || !value.trim()) {
    return { valid: true };
  }
  const compact = value.replace(/\s/g, '').toUpperCase();
  if (compact.length < TURKISH_PLATE_MIN_LENGTH) {
    return {
      valid: false,
      message: `Plaka en az ${TURKISH_PLATE_MIN_LENGTH} karakter olmalıdır (il kodu + harf + rakam).`,
    };
  }
  if (compact.length > TURKISH_PLATE_MAX_LENGTH) {
    return {
      valid: false,
      message: `Plaka en fazla ${TURKISH_PLATE_MAX_LENGTH} karakter olmalıdır (örn: 34 ABC 1234).`,
    };
  }
  if (!TURKISH_PLATE_REGEX.test(compact)) {
    return {
      valid: false,
      message: 'Plaka formatı: 2 rakam (il kodu 01-81) + 1-3 harf + 2-4 rakam (örn: 34 ABC 1234).',
    };
  }
  return { valid: true };
}

export function normalizePlateInput(value: string): string {
  const filtered = value.replace(/[^0-9A-Za-z\s]/g, '').toUpperCase();
  if (filtered.length > TURKISH_PLATE_INPUT_MAX_LENGTH) {
    return filtered.slice(0, TURKISH_PLATE_INPUT_MAX_LENGTH);
  }
  return filtered;
}

const VKN_LENGTH = 10;
const VKN_DIGITS_ONLY = /^\d{10}$/;

function calcVknCheckDigit(digits: string): number {
  let s = 0;
  const rev = digits.slice(0, 9).split('').reverse();
  for (let i = 1; i <= 9; i++) {
    const n = parseInt(rev[i - 1], 10);
    const c1 = (n + i) % 10;
    if (c1 !== 0) {
      let c2 = (c1 * Math.pow(2, i)) % 9;
      if (c2 === 0) c2 = 9;
      s += c2;
    }
  }
  return ((10 - s) % 10 + 10) % 10;
}

export function validateVkn(value: string): { valid: boolean; message?: string } {
  if (!value || !value.trim()) return { valid: true };
  const str = value.replace(/\s/g, '');
  if (!/^\d+$/.test(str)) {
    return { valid: false, message: 'Vergi numarası sadece rakam içermelidir.' };
  }
  if (str.length !== VKN_LENGTH) {
    return {
      valid: false,
      message: `Vergi Kimlik Numarası (VKN) ${VKN_LENGTH} rakam olmalıdır.`,
    };
  }
  const expected = calcVknCheckDigit(str);
  const actual = parseInt(str[9], 10);
  if (actual !== expected) {
    return {
      valid: false,
      message: 'Vergi Kimlik Numarası (VKN) kontrol basamağı geçersiz.',
    };
  }
  return { valid: true };
}

const TR_IBAN_REGEX = /^TR\d{24}$/;

export function validateTckn(value: string): { valid: boolean; message?: string } {
  if (!value || !value.trim()) return { valid: true };
  const str = value.replace(/\s/g, '');
  if (!/^\d{11}$/.test(str)) {
    return { valid: false, message: 'T.C. Kimlik Numarası (TCKN) 11 rakam olmalıdır.' };
  }
  if (str[0] === '0') {
    return { valid: false, message: 'T.C. Kimlik Numarası 0 ile başlayamaz.' };
  }

  const digits = str.split('').map((d) => parseInt(d, 10));
  const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const evenSum = digits[1] + digits[3] + digits[5] + digits[7];
  const tenth = ((oddSum * 7 - evenSum) % 10 + 10) % 10;
  if (digits[9] !== tenth) {
    return { valid: false, message: 'T.C. Kimlik Numarası kontrol basamağı geçersiz.' };
  }

  const eleventh = digits.slice(0, 10).reduce((sum, digit) => sum + digit, 0) % 10;
  if (digits[10] !== eleventh) {
    return { valid: false, message: 'T.C. Kimlik Numarası kontrol basamağı geçersiz.' };
  }

  return { valid: true };
}

export function validateVknOrTckn(
  value: string,
  required = true,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required
      ? { valid: false, message: 'Vergi Kimlik Numarası (VKN / TCKN) zorunludur.' }
      : { valid: true };
  }

  const digits = trimmed.replace(/\s/g, '');
  if (!/^\d+$/.test(digits)) {
    return { valid: false, message: 'Vergi numarası yalnızca rakam içermelidir.' };
  }

  if (digits.length === VKN_LENGTH) {
    return validateVkn(digits);
  }
  if (digits.length === 11) {
    return validateTckn(digits);
  }

  return {
    valid: false,
    message: 'Vergi Kimlik Numarası 10 haneli (VKN) veya 11 haneli (TCKN) olmalıdır.',
  };
}

export function validateMersisOrTradeRegistry(
  value: string,
  required = true,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required
      ? { valid: false, message: 'MERSİS veya ticaret sicil numarası zorunludur.' }
      : { valid: true };
  }

  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length !== 16) {
      return { valid: false, message: 'MERSİS numarası 16 rakam olmalıdır.' };
    }
    return { valid: true };
  }

  if (trimmed.length < 4 || trimmed.length > 30) {
    return {
      valid: false,
      message: 'Ticaret sicil numarası 4–30 karakter arasında olmalıdır.',
    };
  }

  if (!/^[A-Za-z0-9\-/. ]+$/.test(trimmed)) {
    return {
      valid: false,
      message: 'Ticaret sicil numarası yalnızca harf, rakam ve -/. karakterlerini içerebilir.',
    };
  }

  return { valid: true };
}

export function validateTurkishLandlinePhone(
  value: string,
  required = true,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required
      ? { valid: false, message: 'Sabit telefon numarası zorunludur.' }
      : { valid: true };
  }

  if (/[a-zA-Z]/.test(trimmed)) {
    return { valid: false, message: 'Telefon numarası yalnızca rakam içermelidir.' };
  }

  let digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length > 10) {
    digits = digits.slice(2);
  }
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  if (digits.length !== 10) {
    return {
      valid: false,
      message: 'Sabit telefon numarası 10 haneli olmalıdır (örn: 322 123 45 67).',
    };
  }

  const areaCode = parseInt(digits.slice(0, 3), 10);
  if (areaCode < 212 || areaCode > 488) {
    return {
      valid: false,
      message: 'Geçerli bir alan kodu giriniz (212–488 arası).',
    };
  }

  return { valid: true };
}

export function validateEmailAddress(
  value: string,
  required = true,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required ? { valid: false, message: 'E-posta adresi zorunludur.' } : { valid: true };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, message: 'Geçerli bir e-posta adresi giriniz.' };
  }

  return { valid: true };
}

export function validateTaxOffice(
  value: string,
  required = true,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required ? { valid: false, message: 'Vergi dairesi zorunludur.' } : { valid: true };
  }

  if (trimmed.length < 2 || trimmed.length > 100) {
    return { valid: false, message: 'Vergi dairesi 2–100 karakter arasında olmalıdır.' };
  }

  return { valid: true };
}

export function validateRequiredText(
  value: string,
  label: string,
  minLength = 2,
  maxLength = 255,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return { valid: false, message: `${label} zorunludur.` };
  }
  if (trimmed.length < minLength) {
    return { valid: false, message: `${label} en az ${minLength} karakter olmalıdır.` };
  }
  if (trimmed.length > maxLength) {
    return { valid: false, message: `${label} en fazla ${maxLength} karakter olabilir.` };
  }
  return { valid: true };
}

export function validateFormDate(value: string): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return { valid: false, message: 'Form tarihi zorunludur.' };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return { valid: false, message: 'Tarih YYYY-MM-DD formatında olmalıdır.' };
  }

  const parsed = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return { valid: false, message: 'Geçerli bir tarih giriniz.' };
  }

  const today = new Date();
  today.setHours(23, 59, 59, 999);
  if (parsed > today) {
    return { valid: false, message: 'Form tarihi gelecekte olamaz.' };
  }

  return { valid: true };
}

export function normalizeIbanInput(value: string): string {
  return value.replace(/\s/g, '').toUpperCase().slice(0, 26);
}

export function normalizeDigitsInput(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

export function validateIban(
  value: string,
  required: boolean,
): { valid: boolean; message?: string } {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return required
      ? { valid: false, message: 'IBAN zorunludur.' }
      : { valid: true };
  }
  const normalized = trimmed.replace(/\s/g, '').toUpperCase();
  if (normalized.length !== 26) {
    return {
      valid: false,
      message: 'IBAN TR formatında 26 karakter olmalıdır (TR + 24 rakam).',
    };
  }
  if (!TR_IBAN_REGEX.test(normalized)) {
    return {
      valid: false,
      message: 'IBAN TR ile başlamalı ve 24 rakam içermelidir (örn: TR330006100519786457841326).',
    };
  }
  return { valid: true };
}
